/* eslint-disable max-len */
import { LitElement, html } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import Slider from '../../slider';

@customElement('review-highlights')
class ReviewHighlights extends LitElement {
  @property({ type: Number, attribute: 'product-id' })
  accessor productId;

  @property({ type: String, attribute: 'yotpo-api-key' })
  accessor yotpoApiKey;

  @state()
  accessor reviews = [];

  @state()
  accessor bottomline = {};

  sliderInstance;

  createRenderRoot() {
    return this;
  }

  connectedCallback() {
    super.connectedCallback();
    this.fetchReviews();
    window.addEventListener('swatch:change', this.handleSwatchChange);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    window.removeEventListener('swatch:change', this.handleSwatchChange);
  }

  handleSwatchChange = (e) => {
    const newProductId = Number(e.detail.product.id);
    if (newProductId !== this.productId) {
      this.productId = newProductId;
      this.fetchReviews();
    }
  };

  updated(changedProperties) {
    if (changedProperties.has('reviews')) {
      this.initializeSlider();
    }
  }

  initializeSlider() {
    if (this.reviews.length === 0) {
      return;
    }

    if (this.sliderInstance) {
      this.sliderInstance.destroy();
    }

    const sliderOptions = {
      slidesPerView: 1,
      autoHeight: true,
      allowTouchMove: true,
    };

    if (this.reviews.length > 1) {
      sliderOptions.pagination = {
        el: '.swiper-pagination',
        type: 'bullets',
        clickable: true,
        bulletClass:
          'swiper-pagination-bullet w-2 h-2 rounded-full block bg-gray-200',
        bulletActiveClass: 'swiper-pagination-bullet-active bg-gray-800',
      };
    }

    this.sliderInstance = new Slider({
      el: this.querySelector('[data-review-highlights-carousel]'),
      options: sliderOptions,
    });
  }

  fetchReviews = async () => {
    if (!this.productId || !this.yotpoApiKey) return;

    const baseUrl = `https://api-cdn.yotpo.com/v1/widget/${this.yotpoApiKey}/products/${this.productId}/reviews.json?sort=rating&per_page=3`;
    try {
      const response = await fetch(baseUrl);
      const json = await response.json();
      if (json?.response?.reviews?.length) {
        this.reviews = json.response.reviews;
        this.bottomline = json.response.bottomline;
      } else {
        this.reviews = [];
      }
    } catch (error) {
      console.error('Error fetching reviews:', error);
      this.reviews = [];
    }
  };

  renderPagination = () => {
    if (!this.reviews.length === 1) {
      return '';
    }

    return html` <div class="swiper-pagination h-2 flex gap-x-1 z-1"></div> `;
  };

  renderStars = (score) => {
    const scoreNum = Number(score);
    const wholeStars = Math.floor(scoreNum);
    const halfStar = !Number.isInteger(scoreNum);
    const emptyStars = 5 - Math.ceil(scoreNum);

    return html`
      <div class="flex gap-0.5 mb-2">
        ${Array.from(
          { length: wholeStars },
          () =>
            html`<svg
              xmlns="http://www.w3.org/2000/svg"
              width="16"
              height="15"
              viewBox="0 0 16 15"
              fill="none"
            >
              <path
                fill="currentColor"
                d="M8 0l1.88 5.411 5.728.117-4.565 3.46 1.66 5.484L8 11.2l-4.702 3.272L4.957 8.99.392 5.528l5.727-.117L8 0z"
              ></path>
            </svg>`,
        )}
        ${halfStar
          ? html`<svg
              xmlns="http://www.w3.org/2000/svg"
              class="text-gray-500"
              width="16"
              height="15"
              viewBox="0 0 16 15"
              fill="none"
            >
              <path
                d="M8 0L9.88091 5.41115L15.6085 5.52786L11.0434 8.98885L12.7023 14.4721L8 11.2L3.29772 14.4721L4.95662 8.98885L0.391548 5.52786L6.11909 5.41115L8 0Z"
                fill="currentColor"
              />
              <mask
                id="mask0_2205_11001"
                style="mask-type:alpha"
                maskUnits="userSpaceOnUse"
                x="8"
                y="0"
                width="8"
                height="16"
              >
                <rect
                  width="8"
                  height="16"
                  transform="matrix(-1 0 0 1 16 0)"
                  fill="currentColor"
                />
              </mask>
              <g mask="url(#mask0_2205_11001)">
                <path
                  d="M8 0L6.11909 5.41115L0.391548 5.52786L4.95662 8.98885L3.29772 14.4721L8 11.2L12.7023 14.4721L11.0434 8.98885L15.6085 5.52786L9.88091 5.41115L8 0Z"
                  fill="#ABA9A8"
                />
              </g>
            </svg>`
          : ''}
        ${Array.from(
          { length: emptyStars },
          () =>
            html`<svg
              xmlns="http://www.w3.org/2000/svg"
              class="text-gray-500"
              width="16"
              height="15"
              viewBox="0 0 16 15"
              fill="none"
            >
              <path
                fill="currentColor"
                d="M8 0l1.88 5.411 5.728.117-4.565 3.46 1.66 5.484L8 11.2l-4.702 3.272L4.957 8.99.392 5.528l5.727-.117L8 0z"
              ></path>
            </svg>`,
        )}
      </div>
    `;
  };

  renderReview = (review) => html`
    <div class="swiper-slide">
      <div class="bg-core-1 p-6 ${this.reviews.length > 1 ? 'mb-4' : ''}">
        ${this.renderStars(review.score)}
        <h5 class="body-2--medium mb-1">${review.title}</h5>
        <p class="body-3 mb-2">${review.content}</p>
        <a
          href="#Reviews"
          class="text-text-default text-xs leading-relaxed underline block text-left font-GTAmerica"
          >(${this.bottomline.total_review} Reviews)</a
        >
      </div>
    </div>
  `;

  renderReviews = () => this.reviews.map((review) => this.renderReview(review));

  render() {
    if (!this.reviews.length) {
      return html``;
    }

    return html`
      <div class="mb-4">
        <div class="swiper swiper-container" data-review-highlights-carousel>
          <div class="swiper-wrapper">${this.renderReviews()}</div>
          ${this.renderPagination()}
        </div>
      </div>
    `;
  }
}

export default ReviewHighlights;
